class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => { if (event.target.id === 'CartDrawer-Overlay') this.close(); });
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (!this.classList.contains('gf-v2-drawer') || this.gestureAbort) return;
    this.gestureAbort = new AbortController();
    const options = { signal: this.gestureAbort.signal };
    this.addEventListener('pointerdown', (event) => {
      const region = event.target.closest('.drawer__header, .cart__empty-text');
      if (!this.classList.contains('active') || !matchMedia('(max-width: 749px)').matches || event.pointerType !== 'touch' || event.isPrimary === false || !region || event.clientX < 24 || event.target.closest('button,a,input,select,textarea,label')) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, inner: this.querySelector('.drawer__inner'), region };
    }, options);
    this.addEventListener('pointermove', (event) => {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.locked) {
        if (Math.abs(dy) > 10 && Math.abs(dy) >= Math.abs(dx) || dx < -10) { this.resetDrag(); return; }
        if (dx < 14 || dx < Math.abs(dy) * 1.5) return;
        drag.locked = true;
        drag.region.setPointerCapture?.(event.pointerId);
        drag.inner.style.transition = 'none';
      }
      drag.dx = Math.max(0, dx);
      drag.inner.style.transform = `translateX(${drag.dx}px)`;
    }, options);
    this.addEventListener('pointerup', (event) => {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      const dismiss = drag.locked && drag.dx >= Math.max(72, drag.inner.clientWidth * 0.25);
      this.resetDrag();
      if (dismiss) this.close();
    }, options);
    this.addEventListener('pointercancel', () => this.resetDrag(), options);
  }

  resetDrag() {
    const drag = this.drag;
    if (!drag) return;
    this.drag = null;
    if (drag.region.hasPointerCapture?.(drag.id)) drag.region.releasePointerCapture(drag.id);
    drag.inner.style.removeProperty('transition');
    drag.inner.style.removeProperty('transform');
  }

  disconnectedCallback() {
    this.gestureAbort?.abort(); this.gestureAbort = null;
    clearTimeout(this.openTimer); clearTimeout(this.renderTimer);
    this.resetDrag();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    window.GFRestore?.refreshIfNeeded();
    if (this.classList.contains('active')) {
      const inner = this.querySelector('.drawer__inner');
      trapFocus(inner, inner);
      return;
    }
    if (this.openTimer) return;
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      this.focusAfterOpen = () => {
        if (!this.classList.contains('active')) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close(options = {}) {
    clearTimeout(this.openTimer); this.openTimer = null;
    clearTimeout(this.renderTimer);
    this.resetDrag();
    if (this.focusAfterOpen) this.removeEventListener('transitionend', this.focusAfterOpen);
    this.classList.remove('active');
    removeTrapFocus(options.restoreFocus === false ? undefined : this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.dispatchEvent(new CustomEvent('gf-v2:cart-rendered', { bubbles: true, detail: parsedState }));

    clearTimeout(this.renderTimer);
    this.renderTimer = setTimeout(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    // A recommendation form is replaced after adding; keep a live opener outside the drawer.
    if (this.classList.contains('gf-v2-drawer') && this.contains(element)) {
      this.activeElement ||= document.querySelector('#cart-icon-bubble');
      return;
    }
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
